import { useEffect, useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid-premium';
import OutboxIcon from '@mui/icons-material/Outbox';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import tableHelper from 'src/utils/tableHelper';
import { useTableSettings } from '../tableSettings/useTableSettings';
import type { tableSettingsProps } from '../tableSettings/useTableSettings';

export type Identifier = string | number;

export type WarehouseItemLocation = {
    LocationId: Identifier;
    LocationLabel: string;
    PartitionName?: string | null;
    LastCountDate?: string | null;
    LastCountBy?: string | null;
    Quantity: number;
    QuantityReserved: number;
};

export type StockCorrectionItem = {
    StorageItemId: Identifier;
    WarehouseItemId: Identifier;
    ProductId: Identifier;
    ManufacturerProductId?: Identifier | null;
    ProductName: string;
    WarehouseId: Identifier;
    WarehouseName: string;
    WarehouseQty: number;
    WarehouseItemLocations: WarehouseItemLocation[];
    DefaultPictureResourceId?: Identifier | null;
};

const storageItemIdPath = ['WarehouseItemId'];
const locationIdPath = ['LocationId'];

// The search endpoint supplies these rows; this hook does not fetch a main table.
export const useStocktakingTable = (rows: StockCorrectionItem[], onSelect: (row: StockCorrectionItem) => void): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
    const columns = useMemo<GridColDef[]>(() => [
        { field: 'select', headerName: 'Choose', width: 85, sortable: false, filterable: false, disableColumnMenu: true, renderCell: (params) => <CustomButton iconButton icon={<OutboxIcon />} tooltipText={`Choose ${params.row.ProductId}, storage item ${params.row.StorageItemId}`} onClick={(event) => { event.stopPropagation(); onSelect(params.row as StockCorrectionItem); }} /> },
        { field: 'ProductId', headerName: 'Product ID', minWidth: 130 },
        { field: 'ManufacturerProductId', headerName: 'Manufacturer Product ID', minWidth: 210 },
        { field: 'ProductName', headerName: 'Product', minWidth: 220, flex: 1 },
        { field: 'WarehouseQty', headerName: 'Warehouse qty', type: 'number', minWidth: 135 },
        { field: 'WarehouseName', headerName: 'Warehouse', minWidth: 190 },
        { field: 'StorageItemId', headerName: 'Storage item ID', minWidth: 140 },
        { field: 'WarehouseItemId', headerName: 'Warehouse item ID', minWidth: 165 },
    ], [onSelect]);
    const tableSettings = useTableSettings({ columns, idPath: storageItemIdPath, rows, tableName: 'StockCorrectionMatchesTable' });
    tableSettings.hasToolbar = false;
    tableSettings.autoHeight = true;
    tableSettings.loading = false;

    useEffect(() => {
        const selection = tableSettings.rowSelectionModel ?? [];
        if (!Array.isArray(selection) || selection.length === 0) return;
        const selectedRow = rows.find((row) => String(row.WarehouseItemId) === String(selection[0]));
        tableSettings.setRowSelectionModel?.([]);
        if (selectedRow) onSelect(selectedRow);
    }, [tableSettings.rowSelectionModel, rows, onSelect]);

    return [columns, tableSettings];
};

// Shared by the form card and the expanded rows in the selection dialog.
export const useWarehouseItemLocationsTable = (rows: WarehouseItemLocation[], tableName = 'StockCorrectionLocationsTable'): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
    const columns = useMemo<GridColDef[]>(() => [
        { field: 'LocationLabel', headerName: 'Location', minWidth: 190, flex: 1 },
        { field: 'Quantity', headerName: 'Quantity', type: 'number', minWidth: 105 },
        { field: 'QuantityReserved', headerName: 'Reserved', type: 'number', minWidth: 105 },
        { field: 'PartitionName', headerName: 'Partition', minWidth: 155 },
        { field: 'LastCountDate', headerName: 'Last counted', type: 'dateTime', minWidth: 175, valueGetter: tableHelper.getDateColumn },
        { field: 'LastCountBy', headerName: 'Counted by', minWidth: 170 },
        { field: 'LocationId', headerName: 'Location ID', minWidth: 115 },
    ], []);
    const tableSettings = useTableSettings({ columns, idPath: locationIdPath, rows, tableName });
    tableSettings.hasToolbar = false;
    tableSettings.autoHeight = true;
    tableSettings.loading = false;
    tableSettings.hideFooter = true;

    return [columns, tableSettings];
};
